'use client';

import { FieldKind } from '@inixiative/json-rules';
import { type ArrayNode, type BuilderNode, facetId, type GroupNode, type LeafNode } from '@inixiative/rules-builder';
import { Button } from '@zealot/ui/components/button';
import { ItemIcon } from '@zealot/ui/components/combobox-select';
import { Input } from '@zealot/ui/components/input';
import { SimpleSelect } from '@zealot/ui/components/simple-select';
import { ValueInput } from '@zealot/ui/components/value-input';
import { cn } from '@zealot/ui/lib/utils';
import { ChevronRight, Plus } from 'lucide-react';
import { type ReactNode, useContext, useMemo, useState } from 'react';
import {
  CombinatorSelect,
  DateWindowControl,
  MatchToggle,
  OperatorValue,
  PresetChip,
  RangeValue,
  RemoveButton,
  VariableSlotControl,
} from './controls';
import {
  BuilderContext,
  type BuilderScope,
  commitDeferred,
  conditionSummary,
  findDateWindowLeaf,
  isLeafComplete,
  isRangeShape,
  lockedConditionSummary,
  lockedConditions,
  presetRowClasses,
  relabelOperators,
  relationModelName,
  selectorClause,
  selectorIsVisible,
  selectorMultiCount,
  useNestedScope,
} from './lib';
import { FacetFieldPicker } from './pickers';
import { SelectorControl } from './selector-control';

// gloss
export const GroupBox = ({
  operator,
  nodes,
  addRule,
  addGroup,
  canAddGroup,
  remove,
  nested,
  relation,
  childScope,
}: {
  operator: GroupNode['operator'];
  nodes: BuilderNode[];
  addRule: () => void;
  addGroup: () => void;
  canAddGroup: boolean;
  remove?: () => void;
  nested: boolean;
  relation?: ReactNode;
  childScope?: BuilderScope;
}) => (
  <div
    data-slot="rule-group"
    className={cn(
      'overflow-hidden rounded-lg border border-border bg-muted/20',
      relation && 'border-l-2 border-l-primary',
      nested && 'ml-3.5',
    )}
  >
    {relation ? (
      <div className="flex flex-wrap items-center gap-2 border-border border-b bg-primary/5 px-2.5 py-2">
        {relation}
        <RemoveButton onClick={remove} />
      </div>
    ) : null}
    <div className="flex flex-wrap items-center gap-2 border-border border-b bg-muted/40 px-2.5 py-2">
      {relation ? <span className="text-muted-foreground text-xs">matching</span> : null}
      <MatchToggle operator={operator} />
      <div className="ml-auto flex items-center gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={addRule}>
          + Condition
        </Button>
        {canAddGroup ? (
          <Button type="button" variant="ghost" size="sm" onClick={addGroup}>
            + Group
          </Button>
        ) : null}
        {relation ? null : <RemoveButton onClick={remove} />}
      </div>
    </div>
    <div className="flex flex-col gap-2 p-2.5">
      {childScope ? (
        <BuilderContext.Provider value={childScope}>
          {nodes.map((child) => (
            <Node key={child.id} node={child} />
          ))}
        </BuilderContext.Provider>
      ) : (
        nodes.map((child) => <Node key={child.id} node={child} />)
      )}
    </div>
  </div>
);

// gloss
export const SentenceGroup = ({ node }: { node: GroupNode }) => (
  <div data-slot="rule-group" className="border-border border-l-2 py-0.5 pl-4">
    <div className="flex items-center gap-2 text-muted-foreground text-sm">
      Match
      <CombinatorSelect operator={node.operator} />
      of these:
      <RemoveButton onClick={node.remove} />
    </div>
    <div className="mt-2 flex flex-col gap-2">
      {node.children.map((child) => (
        <Node key={child.id} node={child} />
      ))}
    </div>
    <Button type="button" variant="ghost" size="sm" className="mt-1.5" onClick={node.addRule}>
      <Plus className="size-3.5" />
      Add condition
    </Button>
  </div>
);

// gloss
export const ConditionCard = ({ node }: { node: LeafNode }) => {
  const scope = useContext(BuilderContext);
  const { sentence, collapsible } = scope;
  const [collapsed, setCollapsed] = useState(() => Boolean(collapsible) && isLeafComplete(node));
  const cardClasses = cn(
    'flex flex-wrap items-center gap-2',
    !sentence && 'rounded-md border border-border bg-card px-2.5 py-2 shadow-sm',
  );

  if (collapsible && collapsed) {
    return (
      <div data-slot="rule-leaf" className={cardClasses}>
        {}
        <button
          type="button"
          aria-expanded={false}
          onClick={() => setCollapsed(false)}
          className="flex cursor-pointer items-center gap-2 text-left text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronRight className="size-4 shrink-0" aria-hidden />
          <span className="text-foreground text-sm">{conditionSummary(node, scope) ?? 'Condition'}</span>
        </button>
        <RemoveButton onClick={node.remove} />
      </div>
    );
  }

  return (
    <div data-slot="rule-leaf" className={cardClasses}>
      {collapsible ? (
        <button
          type="button"
          aria-label="Collapse"
          aria-expanded={true}
          onClick={() => setCollapsed(true)}
          className="text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronRight className="size-4 rotate-90" />
        </button>
      ) : null}
      {node.leafKind === 'boolean' ? (
        <ValueInput
          spec={{ kind: FieldKind.Boolean, as: 'select' }}
          value={String(node.literal!.value) === 'true'}
          onChange={(next) => node.literal!.set(next as boolean)}
          placeholder="value"
          size="sm"
          className="w-44"
          aria-label="Value"
        />
      ) : (
        <>
          <FacetFieldPicker
            selected={node.hoist?.id ?? node.field!.value}
            options={node.field!.options}
            set={node.field!.set}
          />
          <OperatorValue node={node} />
        </>
      )}
      <RemoveButton onClick={node.remove} />
    </div>
  );
};

// gloss
export const RelationScope = ({ node }: { node: ArrayNode }) => {
  const { operatorLabels } = useContext(BuilderContext);
  return (
    <>
      <FacetFieldPicker selected={node.field.value} options={node.field.options} set={node.field.set} />
      {}
      <SimpleSelect
        aria-label="Match"
        items={relabelOperators(node.arrayOperator!.options, operatorLabels)}
        value={node.arrayOperator!.value ?? null}
        onValueChange={(next) => next && commitDeferred(() => node.arrayOperator!.set(next))}
        placeholder="match"
        size="sm"
        className="w-32"
      />
      {node.count ? (
        <Input
          aria-label="Count"
          size="sm"
          className="w-16"
          type="number"
          value={node.count.value ?? ''}
          onChange={(event) => {
            const count = Number(event.target.value);
            node.count?.set(event.target.value === '' || !Number.isFinite(count) ? undefined : count);
          }}
        />
      ) : null}
    </>
  );
};

// gloss
export const PresetRow = ({ node }: { node: ArrayNode | GroupNode | LeafNode }) => {
  const { sentence } = useContext(BuilderContext);
  return (
    <div data-slot="rule-preset" className={presetRowClasses(sentence)}>
      <PresetChip icon={node.hoist?.icon} label={node.hoist?.label ?? ''} />
      {(node.variables ?? []).map((slot) => (
        <VariableSlotControl key={JSON.stringify(slot.path)} slot={slot} />
      ))}
      {node.remove ? <RemoveButton onClick={node.remove} /> : null}
    </div>
  );
};

// gloss
export const AggregateRow = ({ node }: { node: ArrayNode }) => {
  const { sentence, operatorLabels, decoration, source } = useContext(BuilderContext);
  const aggregate = node.aggregate!;
  const model = relationModelName(source, node.field.value ?? '');
  const aggregateFieldLabel =
    (model ? decoration?.labels?.fields?.[`${model}.${aggregate.field.value}`]?.label : undefined) ??
    aggregate.field.options.find((o) => o.value === aggregate.field.value)?.label ??
    aggregate.field.value ??
    '';
  const current = aggregate.value.current;
  const dateLeaf = findDateWindowLeaf(node.condition);

  return (
    <div data-slot="rule-preset" className={presetRowClasses(sentence)}>
      <PresetChip label={`Total ${aggregateFieldLabel}`} />
      <SimpleSelect
        aria-label="Operator"
        items={relabelOperators(aggregate.operator.options, operatorLabels)}
        value={aggregate.operator.value ?? null}
        onValueChange={(next) => next && commitDeferred(() => aggregate.operator.set(next))}
        placeholder="operator"
        size="sm"
        className="w-40"
      />
      {isRangeShape(aggregate.value.shape) ? (
        <RangeValue value={aggregate.value} kind="Int" dashedWhenEmpty />
      ) : (
        <Input
          type="number"
          aria-label="Threshold"
          value={typeof current === 'number' ? String(current) : ''}
          onChange={(event) => {
            const raw = event.target.value;
            aggregate.value.set(raw === '' ? undefined : Number(raw));
          }}
          placeholder="amount"
          className={cn('h-9 w-28', typeof current !== 'number' && 'border-dashed')}
        />
      )}
      <span className="text-muted-foreground text-xs">{aggregateFieldLabel}</span>
      <DateWindowControl node={node} leaf={dateLeaf} />
      <RemoveButton onClick={node.remove} />
    </div>
  );
};

// gloss
export const FacetRow = ({ node }: { node: ArrayNode }) => {
  const scope = useContext(BuilderContext);
  const { sentence, operatorLabels, collapsible, sourceValues, source, decoration } = scope;
  const nestedScope = useNestedScope(node);

  const quantifierItems = useMemo(() => {
    const labels = (
      (decoration?.facets ?? []).find((facet) => facetId(facet) === node.hoist?.id) as
        | { quantifierLabels?: { any?: string; none?: string } }
        | undefined
    )?.quantifierLabels;
    return labels?.any && labels.none
      ? [
          { value: 'any', label: labels.any },
          { value: 'none', label: labels.none },
        ]
      : null;
  }, [decoration, node.hoist?.id]);
  const quantifier = node.arrayOperator?.value ?? 'any';
  const verbSelect =
    quantifierItems && (quantifier === 'any' || quantifier === 'none') ? (
      <SimpleSelect
        aria-label="Include or exclude"
        items={quantifierItems}
        value={quantifier}
        onValueChange={(next) => next && commitDeferred(() => node.arrayOperator?.set(next))}
        size="sm"
        className="w-fit min-w-36"
      />
    ) : null;
  const predicate = node.condition;
  // why: this component relies on rules-builder normalizing identity clauses out of the tree
  // why: before render — if that upstream guarantee regresses, ZLT-3899 recurs
  const children = predicate?.children ?? [];
  const identity = lockedConditions(node, scope.decoration);
  const selectorFields = new Set((node.selectors ?? []).map((selector) => selector.field));
  const selectorClauses = new Set<BuilderNode>(
    (node.selectors ?? []).flatMap((selector) => selectorClause(node, selector) ?? []),
  );
  const editable = children.filter((child) => !selectorClauses.has(child));
  const [first] = editable;
  const rowLeaf =
    editable.length === 1 && first?.kind === 'leaf' && !(first.field?.value && selectorFields.has(first.field.value))
      ? first
      : undefined;
  const hasVisibleSelector = useMemo(
    () =>
      (node.selectors ?? []).some((selector) =>
        selectorIsVisible(node, selector, sourceValues, source, scope.decoration),
      ),
    [node, sourceValues, source, scope.decoration],
  );

  const [primarySelector] = node.selectors ?? [];
  const primaryMulti = primarySelector ? selectorMultiCount(node, primarySelector) : undefined;
  const selectorValue = primarySelector
    ? primaryMulti
      ? `${primaryMulti} selected`
      : selectorClause(node, primarySelector)?.value?.current
    : undefined;
  const facetLabel = node.hoist?.label ?? node.field.value ?? '';
  const conditionCount = `${editable.length} condition${editable.length === 1 ? '' : 's'}`;
  const summary = rowLeaf
    ? (conditionSummary(rowLeaf, nestedScope, selectorValue != null ? String(selectorValue) : undefined) ?? facetLabel)
    : selectorValue != null
      ? `${facetLabel} is ${selectorValue}${editable.length ? ` (${conditionCount})` : ''}`
      : editable.length
        ? `${facetLabel} (${conditionCount})`
        : facetLabel;

  const selectorsComplete = (node.selectors ?? []).every((selector) => {
    if (selectorMultiCount(node, selector)) return true;
    const current = selectorClause(node, selector)?.value?.current;
    return current != null && current !== '';
  });
  const isComplete = selectorsComplete && (!rowLeaf || isLeafComplete(rowLeaf));
  const [expanded, setExpanded] = useState(() => (collapsible ? !isComplete : false));
  const lineAddTarget = !rowLeaf && !editable.length ? predicate : undefined;

  if (collapsible && !expanded) {
    return (
      <div
        data-slot="rule-facet"
        className={cn(
          'flex flex-wrap items-center gap-2',
          !sentence && 'rounded-md border border-border bg-card px-2.5 py-2 shadow-sm',
        )}
      >
        {}
        <button
          type="button"
          aria-expanded={false}
          onClick={() => setExpanded(true)}
          className="flex cursor-pointer items-center gap-2 text-left text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronRight className="size-4 shrink-0" aria-hidden />
          <span className="text-foreground text-sm">{summary}</span>
        </button>
        <RemoveButton onClick={node.remove} />
      </div>
    );
  }

  return (
    <div
      data-slot="rule-facet"
      className={cn(
        'flex flex-col gap-2',
        (!sentence || expanded) && 'rounded-md border border-border bg-card px-2.5 py-2 shadow-sm',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          className="text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronRight className={cn('size-4 transition-transform', expanded && 'rotate-90')} />
        </button>
        <FacetFieldPicker selected={node.hoist!.id} options={node.field.options} set={node.field.set} />
        {verbSelect}
        {(node.selectors ?? []).map((selector) => (
          <SelectorControl key={selector.field} node={node} selector={selector} onPick={() => setExpanded(true)} />
        ))}
        {rowLeaf ? (
          <BuilderContext.Provider value={nestedScope}>
            {}
            {!hasVisibleSelector && rowLeaf.field ? (
              <FacetFieldPicker
                selected={rowLeaf.field.value}
                options={rowLeaf.field.options}
                set={rowLeaf.field.set}
              />
            ) : null}
            <OperatorValue node={rowLeaf} />
          </BuilderContext.Provider>
        ) : lineAddTarget ? (
          <Button type="button" variant="ghost" size="sm" onClick={lineAddTarget.addRule}>
            + Condition
          </Button>
        ) : (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
            className="cursor-pointer text-muted-foreground text-xs underline-offset-2 transition-colors hover:text-foreground hover:underline"
          >
            {conditionCount}
          </button>
        )}
        <RemoveButton onClick={node.remove} />
      </div>
      {expanded ? (
        <div className="flex flex-col gap-2 border-border border-t pt-2 pl-6">
          <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground text-xs">
            {}
            {node.arrayOperator!.options.length && (!collapsible || !node.arrayOperator!.hidden) ? (
              <SimpleSelect
                aria-label="Match"
                items={relabelOperators(node.arrayOperator!.options, operatorLabels)}
                value={node.arrayOperator!.value ?? null}
                onValueChange={(next) => next && commitDeferred(() => node.arrayOperator!.set(next))}
                placeholder="match"
                size="sm"
                className="w-24"
              />
            ) : (
              <span className="font-medium">
                {(node.arrayOperator!.value && operatorLabels?.[node.arrayOperator!.value]) ??
                  node.arrayOperator!.value}
              </span>
            )}
            <span>of</span>
            <span>{node.hoist?.label ?? node.field.value}</span>
            {}
            {!collapsible && identity.length ? (
              <>
                <span>where</span>
                {identity.map((clause) => (
                  <span key={String(clause.field)} className="rounded bg-muted px-1.5 py-0.5">
                    {lockedConditionSummary(clause, nestedScope)}
                  </span>
                ))}
              </>
            ) : null}
          </div>
          {predicate ? (
            <BuilderContext.Provider value={nestedScope}>
              {!rowLeaf && editable.length > 1 ? <MatchToggle operator={predicate.operator} /> : null}
              {!rowLeaf && editable.map((child) => <Node key={child.id} node={child} />)}
              {lineAddTarget ? null : (
                <Button type="button" variant="ghost" size="sm" className="self-start" onClick={predicate.addRule}>
                  + Condition
                </Button>
              )}
            </BuilderContext.Provider>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// gloss
export const ArrayRow = ({ node }: { node: ArrayNode }) => {
  const nestedScope = useNestedScope(node);
  if (node.atomic) return <PresetRow node={node} />;
  if (node.aggregate) return <AggregateRow node={node} />;
  if (node.hoist) return <FacetRow node={node} />;
  const predicate = node.condition;
  if (!predicate) {
    return (
      <div
        data-slot="rule-array"
        className={cn(
          'overflow-hidden rounded-lg border border-border border-l-2 border-l-primary bg-muted/20',
          node.depth > 0 && 'ml-3.5',
        )}
      >
        <div className="flex flex-wrap items-center gap-2 bg-primary/5 px-2.5 py-2">
          <RelationScope node={node} />
          <div className="ml-auto">
            <RemoveButton onClick={node.remove} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <GroupBox
      operator={predicate.operator}
      nodes={predicate.children}
      addRule={predicate.addRule}
      addGroup={predicate.addGroup}
      canAddGroup={predicate.canAddGroup}
      remove={node.remove}
      nested={node.depth > 0}
      relation={<RelationScope node={node} />}
      childScope={nestedScope}
    />
  );
};

// gloss
export const Node = ({ node }: { node: BuilderNode }) => {
  const { sentence } = useContext(BuilderContext);
  if (node.kind !== 'array' && node.atomic) return <PresetRow node={node} />;
  if (node.kind === 'group' && sentence && !node.hoist) return <SentenceGroup node={node} />;
  if (node.kind === 'group')
    return (
      <GroupBox
        operator={node.operator}
        nodes={node.children}
        addRule={node.addRule}
        addGroup={node.addGroup}
        canAddGroup={node.canAddGroup}
        remove={node.remove}
        nested={node.depth > 0}
        relation={
          node.hoist ? (
            <span className="flex items-center gap-1.5 font-medium text-sm">
              {node.hoist.icon ? <ItemIcon icon={node.hoist.icon} label={node.hoist.label} /> : null}
              {node.hoist.label}
            </span>
          ) : undefined
        }
      />
    );
  if (node.kind === 'array') return <ArrayRow node={node} />;
  return <ConditionCard node={node} />;
};
